import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import ConsultationModal from '../../components/feature/ConsultationModal';

const AtrialFibrillation = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const symptoms = [
    { icon: 'ri-heart-pulse-line', title: 'Irregular Heartbeat', description: 'A fluttering or racing sensation in your chest, often described as your heart "skipping beats"' },
    { icon: 'ri-run-line', title: 'Fatigue & Weakness', description: 'Feeling unusually tired or weak, even during normal daily activities' },
    { icon: 'ri-lungs-line', title: 'Shortness of Breath', description: 'Difficulty breathing, especially during physical activity or when lying down' },
    { icon: 'ri-flashlight-line', title: 'Dizziness', description: 'Feeling lightheaded, faint, or experiencing episodes of near-fainting' },
    { icon: 'ri-heart-3-line', title: 'Chest Discomfort', description: 'Pain, pressure, or discomfort in the chest area during episodes' },
    { icon: 'ri-drop-line', title: 'Reduced Exercise Tolerance', description: 'Inability to exercise at your usual level without becoming exhausted' }
  ];

  const riskFactors = [
    { title: 'Age', description: 'Risk increases significantly after age 60', icon: 'ri-calendar-line' },
    { title: 'High Blood Pressure', description: 'The most common risk factor for AFib', icon: 'ri-heart-pulse-line' },
    { title: 'Heart Disease', description: 'Previous heart conditions increase risk', icon: 'ri-heart-line' },
    { title: 'Obesity', description: 'Excess weight strains the heart', icon: 'ri-scales-3-line' },
    { title: 'Diabetes', description: 'Blood sugar issues affect heart health', icon: 'ri-test-tube-line' },
    { title: 'Sleep Apnea', description: 'Breathing interruptions stress the heart', icon: 'ri-moon-line' }
  ];

  const treatments = [
    {
      title: 'Medication Therapy',
      description: 'Blood thinners to prevent clots, rate control medications to slow heart rate, and rhythm control drugs to restore normal rhythm.',
      icon: 'ri-capsule-line',
      image: 'https://public.readdy.ai/ai/img_res/c32004b6248cf5e38ef3358f448d3009.jpg'
    },
    {
      title: 'Cardioversion',
      description: 'A procedure that uses electrical shocks or medications to reset your heart rhythm back to its normal pattern.',
      icon: 'ri-flashlight-line',
      image: 'https://readdy.ai/api/search-image?query=Medical%20cardioversion%20procedure%20equipment%20in%20modern%20hospital%20setting%20with%20clean%20clinical%20environment%20soft%20blue%20lighting%20professional%20healthcare%20technology%20minimalist%20composition&width=400&height=300&seq=afib2&orientation=landscape'
    },
    {
      title: 'Catheter Ablation',
      description: 'A minimally invasive procedure that destroys small areas of heart tissue causing the irregular rhythm.',
      icon: 'ri-surgical-mask-line',
      image: 'https://readdy.ai/api/search-image?query=Catheter%20ablation%20medical%20procedure%20illustration%20showing%20heart%20treatment%20modern%20medical%20technology%20clean%20clinical%20aesthetic%20soft%20lighting%20professional%20healthcare%20imagery&width=400&height=300&seq=afib3&orientation=landscape'
    },
    {
      title: 'Lifestyle Changes',
      description: 'Heart-healthy diet, regular exercise, stress management, limiting alcohol and caffeine, and maintaining healthy weight.',
      icon: 'ri-heart-add-line',
      image: 'https://readdy.ai/api/search-image?query=Healthy%20lifestyle%20concept%20with%20fresh%20vegetables%20fruits%20exercise%20equipment%20yoga%20mat%20on%20bright%20clean%20background%20wellness%20heart%20health%20minimalist%20photography&width=400&height=300&seq=afib4&orientation=landscape'
    }
  ];

  const faqs = [
    {
      question: 'Can AFib go away on its own?',
      answer: 'Some people experience paroxysmal AFib, which comes and goes on its own. However, AFib often progresses over time and typically requires treatment to manage symptoms and prevent complications like stroke.'
    },
    {
      question: 'Is AFib life-threatening?',
      answer: 'While AFib itself is usually not immediately life-threatening, it significantly increases the risk of stroke (by 5 times) and can lead to heart failure if left untreated. Proper management is essential.'
    },
    {
      question: 'Can I exercise with AFib?',
      answer: 'Yes, most people with AFib can and should exercise. Regular moderate exercise can actually help manage AFib. However, consult your doctor about appropriate activities and intensity levels for your condition.'
    },
    {
      question: 'How is AFib diagnosed?',
      answer: 'AFib is typically diagnosed through an electrocardiogram (ECG/EKG), which records your heart\'s electrical activity. Your doctor may also use Holter monitors, event recorders, or echocardiograms for comprehensive evaluation.'
    }
  ];

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Abstract%20medical%20background%20with%20subtle%20heartbeat%20rhythm%20pattern%20soft%20gradient%20from%20warm%20coral%20pink%20to%20light%20peach%20tones%20clean%20minimalist%20healthcare%20aesthetic%20gentle%20flowing%20lines%20representing%20heart%20rhythm&width=1920&height=800&seq=afibhero&orientation=landscape"
            alt="AFib Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link 
                to="/topics" 
                className="inline-flex items-center text-sm text-[#0d9488] hover:text-[#f04e58] transition-colors mb-6 cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Knowledge Base
              </Link>
              
              <span className="inline-block px-4 py-1.5 bg-[#f04e58]/10 text-[#f04e58] text-sm font-medium rounded-full mb-6">
                Arrhythmia
              </span>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-gray-900 mb-6 leading-tight">
                Atrial Fibrillation
                <span className="block text-[#f04e58] font-normal">(AFib)</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-xl">
                Atrial fibrillation is the most common type of irregular heartbeat, affecting millions worldwide. Understanding AFib is the first step toward effective management and a healthier heart.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="inline-flex items-center px-8 py-3 bg-[#f04e58] text-white text-sm font-medium rounded-full hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule a Free Consultation
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
                <a 
                  href="#symptoms"
                  className="inline-flex items-center px-8 py-3 border-2 border-gray-900 text-gray-900 text-sm font-medium rounded-full hover:bg-gray-900 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                >
                  Learn More
                </a>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Anatomical%20illustration%20of%20human%20heart%20showing%20atrial%20fibrillation%20electrical%20signals%20modern%20medical%20illustration%20style%20soft%20coral%20and%20red%20tones%20clean%20white%20background%20professional%20healthcare%20educational%20imagery&width=600&height=500&seq=afibheart&orientation=portrait"
                  alt="Heart Illustration"
                  className="w-full h-auto rounded-3xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is AFib Section */}
      <section className="py-20 bg-gradient-to-br from-[#fef3f2] via-white to-[#fde8e7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 border border-[#0d9488] text-[#0d9488] text-xs font-medium rounded-full mb-6 uppercase tracking-wider">
                Medical Overview
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                What Happens During <span className="text-[#f04e58]">Atrial Fibrillation?</span>
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                In a healthy heart, electrical signals travel in an organized pattern, causing the heart to beat regularly. During atrial fibrillation, these signals become chaotic, causing the upper chambers (atria) to quiver instead of contracting effectively.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                This irregular rhythm can cause blood to pool in the atria, potentially forming clots. If a clot travels to the brain, it can cause a stroke. AFib also makes the heart work harder, which can lead to heart failure over time.
              </p>
              
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <div className="text-3xl font-bold text-[#f04e58] mb-1">5x</div>
                  <div className="text-xs text-gray-600">Higher Stroke Risk</div>
                </div>
                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <div className="text-3xl font-bold text-[#0d9488] mb-1">6M+</div>
                  <div className="text-xs text-gray-600">Americans Affected</div>
                </div>
                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <div className="text-3xl font-bold text-gray-900 mb-1">33%</div>
                  <div className="text-xs text-gray-600">Unaware They Have It</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Doctor%20consulting%20with%20patient%20about%20heart%20health%20in%20modern%20bright%20medical%20office%20warm%20professional%20atmosphere%20diverse%20healthcare%20setting%20soft%20natural%20lighting%20compassionate%20care&width=600&height=500&seq=afibdoctor&orientation=portrait"
                alt="Doctor Consultation"
                className="w-full h-auto rounded-3xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms Section */}
      <section id="symptoms" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-[#0d9488]/10 text-[#0d9488] text-xs font-medium rounded-full mb-6 uppercase tracking-wider">
              Recognize the Signs
            </span>
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Common <span className="text-[#f04e58]">Symptoms</span> of AFib
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Some people with AFib have no symptoms at all, while others experience significant discomfort. Recognizing these signs can lead to earlier diagnosis and treatment.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {symptoms.map((symptom, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-[#fef3f2] to-white p-6 rounded-2xl border border-[#f04e58]/10 hover:border-[#f04e58]/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#f04e58]/10 rounded-xl mb-4">
                  <i className={`${symptom.icon} text-2xl text-[#f04e58]`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{symptom.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{symptom.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Factors Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 border border-white/30 text-white text-xs font-medium rounded-full mb-6 uppercase tracking-wider">
                Know Your Risk
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
                Who Is at Risk for <span className="text-[#f04e58]">AFib?</span>
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                While anyone can develop atrial fibrillation, certain factors significantly increase your risk. Understanding these factors can help you take preventive measures and seek early screening.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {riskFactors.map((factor, index) => (
                  <div 
                    key={index}
                    className="bg-white/5 backdrop-blur-sm p-4 rounded-xl border border-white/10 hover:border-[#f04e58]/50 transition-all"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-[#f04e58]/20 rounded-lg mb-3">
                      <i className={`${factor.icon} text-lg text-[#f04e58]`}></i>
                    </div>
                    <h4 className="text-white font-medium mb-1">{factor.title}</h4>
                    <p className="text-xs text-gray-400">{factor.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Senior%20person%20checking%20heart%20rate%20on%20smartwatch%20health%20monitoring%20technology%20modern%20lifestyle%20soft%20warm%20lighting%20clean%20minimalist%20composition%20wellness%20concept&width=600&height=500&seq=afibrisk&orientation=portrait"
                alt="Health Monitoring"
                className="w-full h-auto rounded-3xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-[#0d9488]/10 text-[#0d9488] text-xs font-medium rounded-full mb-6 uppercase tracking-wider">
              Treatment Options
            </span>
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Managing AFib <span className="text-[#f04e58]">Effectively</span>
            </h2>
            <p className="text-gray-600 leading-relaxed">
              AFib is highly treatable. Your treatment plan will depend on your symptoms, overall health, and how long you've had AFib. Here are the main approaches.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {treatments.map((treatment, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={treatment.image}
                    alt={treatment.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg">
                      <i className={`${treatment.icon} text-lg text-[#f04e58]`}></i>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-[#f04e58] transition-colors">
                    {treatment.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {treatment.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-[#fef3f2] via-white to-[#fde8e7]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-[#f04e58]/10 text-[#f04e58] text-xs font-medium rounded-full mb-6 uppercase tracking-wider">
              Common Questions
            </span>
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Frequently Asked <span className="text-[#f04e58]">Questions</span>
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer"
                >
                  <span className="text-lg font-medium text-gray-900 pr-4">{faq.question}</span>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-[#f04e58]/10 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-[#f04e58]"></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Modern%20cardiology%20clinic%20interior%20with%20warm%20welcoming%20atmosphere%20natural%20light%20streaming%20through%20windows%20comfortable%20waiting%20area%20professional%20healthcare%20environment%20soft%20coral%20and%20neutral%20tones&width=1920&height=600&seq=afibcta&orientation=landscape"
            alt="Clinic"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/80 to-gray-900/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Ready to Take Control of Your <span className="text-[#f04e58]">Heart Health?</span>
            </h2>
            <p className="text-gray-300 leading-relaxed mb-8">
              Don't wait for symptoms to worsen. Our cardiology specialists are here to help you understand your risk, get properly diagnosed, and create a personalized treatment plan.
            </p>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center px-8 py-4 bg-[#f04e58] text-white text-sm font-medium rounded-full hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap"
            >
              Schedule Your Free Consultation
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        </div>
      </section>

      <Footer />
      
      <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default AtrialFibrillation;
